<template>
    <div class="p-3">
        <div
            class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
        >
            <div>
                <h3 class="mb-1">Monitoring File Status</h3>
                <div class="text-muted">
                    Monitoring keseluruhan pergerakan file rekam medis.
                </div>
            </div>

            <button
                class="btn btn-danger"
                :disabled="loading"
                @click="printPdf"
            >
                <i class="fa fa-file-pdf-o me-1"></i>
                Print PDF
            </button>
        </div>

        <div class="card border-0 shadow-sm mb-3">
            <div class="card-header bg-white fw-semibold">
                <i class="fa fa-filter text-primary me-1"></i>
                Pencarian Data
            </div>

            <div class="card-body">
                <div class="row g-3">
                    <div class="col-md-4 col-xl-2">
                        <label class="form-label">No. Registrasi</label>
                        <input
                            v-model="filter.Regno"
                            class="form-control"
                            @keyup.enter="applyFilter"
                        />
                    </div>

                    <div class="col-md-4 col-xl-2">
                        <label class="form-label">No. RM</label>
                        <input
                            v-model="filter.Medrec"
                            class="form-control"
                            @keyup.enter="applyFilter"
                        />
                    </div>

                    <div class="col-md-4 col-xl-3">
                        <label class="form-label">Nama Pasien</label>
                        <input
                            v-model="filter.Firstname"
                            class="form-control"
                            @keyup.enter="applyFilter"
                        />
                    </div>

                    <div class="col-md-4 col-xl-2">
                        <label class="form-label">Tanggal Dari</label>
                        <input
                            v-model="filter.date1"
                            type="date"
                            class="form-control"
                        />
                    </div>

                    <div class="col-md-4 col-xl-2">
                        <label class="form-label">Tanggal Sampai</label>
                        <input
                            v-model="filter.date2"
                            type="date"
                            class="form-control"
                        />
                    </div>
                </div>

                <div class="mt-3 d-flex gap-2">
                    <button
                        class="btn btn-primary"
                        :disabled="loading"
                        @click="applyFilter"
                    >
                        <i class="fa fa-search me-1"></i>
                        Cari
                    </button>

                    <button
                        class="btn btn-outline-secondary"
                        :disabled="loading"
                        @click="resetFilter"
                    >
                        <i class="fa fa-refresh me-1"></i>
                        Reset
                    </button>
                </div>
            </div>
        </div>

        <div class="card border-0 shadow-sm">
            <div
                class="card-header bg-white d-flex justify-content-between align-items-center"
            >
                <strong>Monitoring File Status</strong>

                <select
                    v-model.number="perPage"
                    class="form-select form-select-sm per-page"
                    @change="loadData(1)"
                >
                    <option :value="10">10</option>
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                </select>
            </div>

            <div class="card-body p-0 position-relative">
                <div v-if="loading" class="loading-overlay">
                    <div class="text-center">
                        <div class="spinner-border text-primary"></div>
                        <div class="mt-2">Memuat data...</div>
                    </div>
                </div>

                <div class="table-responsive">
                    <table class="table table-bordered table-hover mb-0">
                        <thead class="table-light">
                            <tr>
                                <th>No</th>
                                <th>No.Reg</th>
                                <th>RM</th>
                                <th>Nama Pasien</th>
                                <th>Tgl Reg</th>
                                <th>Jam Reg</th>
                                <th>Tgl Keluar</th>
                                <th>Jam Keluar</th>
                                <th>Keterangan</th>
                                <th>Poli</th>
                                <th>Dokter</th>
                                <th>Tujuan</th>
                                <th>Cara Bayar</th>
                                <th>User</th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr
                                v-for="(item, index) in rows"
                                :key="item.regno + '-' + index"
                            >
                                <td>{{ rowNumber(index) }}</td>
                                <td>{{ show(item.regno) }}</td>
                                <td>{{ show(item.medrec) }}</td>
                                <td>{{ show(item.firstname) }}</td>
                                <td>{{ formatDate(item.regdate) }}</td>
                                <td>{{ formatTime(item.regtime) }}</td>
                                <td>{{ formatDate(item.tanggalkeluar) }}</td>
                                <td>{{ formatTime(item.jamkeluar) }}</td>
                                <td>
                                    <span
                                        class="badge"
                                        :class="statusClass(item.status)"
                                    >
                                        {{ show(item.status) }}
                                    </span>
                                </td>
                                <td>{{ show(item.nmpoli) }}</td>
                                <td>{{ show(item.nmdoc) }}</td>
                                <td>{{ show(item.nmtuju) }}</td>
                                <td>{{ show(item.nmcbayar) }}</td>
                                <td>{{ show(item.validuser) }}</td>
                            </tr>

                            <tr v-if="!loading && rows.length === 0">
                                <td
                                    colspan="14"
                                    class="text-center text-muted py-4"
                                >
                                    Data tidak ditemukan.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="card-footer bg-white">
                <div class="d-flex justify-content-between align-items-center">
                    <small class="text-muted">
                        {{ pagination.from || 0 }}
                        -
                        {{ pagination.to || 0 }}
                        dari
                        {{ pagination.total || 0 }}
                        data
                    </small>

                    <div class="btn-group">
                        <button
                            class="btn btn-sm btn-outline-secondary"
                            :disabled="pagination.current_page <= 1 || loading"
                            @click="loadData(pagination.current_page - 1)"
                        >
                            <i class="fa fa-chevron-left"></i>
                        </button>

                        <button class="btn btn-sm btn-light" disabled>
                            {{ pagination.current_page }}
                            /
                            {{ pagination.last_page }}
                        </button>

                        <button
                            class="btn btn-sm btn-outline-secondary"
                            :disabled="
                                pagination.current_page >=
                                    pagination.last_page || loading
                            "
                            @click="loadData(pagination.current_page + 1)"
                        >
                            <i class="fa fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "MonitoringFileStatus",

    data() {
        const today = this.today();

        return {
            loading: false,
            rows: [],
            perPage: 25,
            filter: {
                Regno: "",
                Medrec: "",
                Firstname: "",
                date1: today,
                date2: today,
            },
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 25,
                from: null,
                to: null,
                total: 0,
            },
        };
    },

    mounted() {
        this.loadData();
    },

    methods: {
        today() {
            const date = new Date();
            const offset = date.getTimezoneOffset();

            return new Date(date.getTime() - offset * 60000)
                .toISOString()
                .substring(0, 10);
        },

        async loadData(page = 1) {
            this.loading = true;

            try {
                const response = await this.axios.get(
                    "/api/monitoring-file-status/monitoring",
                    {
                        params: {
                            ...this.filter,
                            page,
                            per_page: this.perPage,
                        },
                    }
                );

                if (response.data.status !== "success") {
                    throw new Error(response.data.message);
                }

                this.rows = response.data.list.data || [];
                this.pagination = response.data.list;
            } catch (error) {
                this.rows = [];

                this.$swal.fire({
                    title: "Error",
                    text:
                        error.response?.data?.message ||
                        error.message ||
                        "Data gagal dimuat.",
                    icon: "error",
                });
            } finally {
                this.loading = false;
            }
        },

        applyFilter() {
            if (
                this.filter.date1 &&
                this.filter.date2 &&
                this.filter.date2 < this.filter.date1
            ) {
                this.$swal.fire({
                    title: "Peringatan",
                    text: "Tanggal sampai tidak boleh lebih kecil dari tanggal mulai.",
                    icon: "warning",
                });
                return;
            }

            this.loadData(1);
        },

        resetFilter() {
            const today = this.today();

            this.filter = {
                Regno: "",
                Medrec: "",
                Firstname: "",
                date1: today,
                date2: today,
            };

            this.loadData(1);
        },

        printPdf() {
            const query = new URLSearchParams(this.filter);

            window.open(
                `/api/monitoring-file-status/monitoring/pdf?${query.toString()}`,
                "_blank"
            );
        },

        rowNumber(index) {
            return (
                (this.pagination.current_page - 1) * this.pagination.per_page +
                index +
                1
            );
        },

        show(value) {
            return value === null || value === undefined || value === ""
                ? "-"
                : value;
        },

        formatDate(value) {
            if (!value) return "-";

            const text = String(value);
            const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);

            return match ? `${match[3]}/${match[2]}/${match[1]}` : text;
        },

        formatTime(value) {
            if (!value) return "-";

            const match = String(value).match(/(\d{2}):(\d{2})(?::(\d{2}))?/);

            return match ? `${match[1]}:${match[2]}` : String(value);
        },

        statusClass(status) {
            if (!status) {
                return "bg-secondary";
            }

            const text = String(status).toLowerCase();

            if (text.includes("kembali") && !text.includes("belum")) {
                return "bg-success";
            }

            if (text.includes("keluar") || text.includes("pinjam")) {
                return "bg-warning text-dark";
            }

            return "bg-primary";
        },
    },
};
</script>

<style scoped>
.per-page {
    width: 80px;
}

.table {
    font-size: 13px;
    white-space: nowrap;
}

.loading-overlay {
    position: absolute;
    inset: 0;
    z-index: 20;
    min-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.78);
}
</style>
